import { __assign, __awaiter, __extends, __generator, __values } from "tslib";
import { openDB } from 'idb';
import { STORAGE_FAILURE } from '../messages';
import { BaseEventsStore } from './base-events-store';
import { logIdbError } from '../utils/is-abort-error';
export var currentSequenceKey = 'sessionCurrentSequence';
export var sequencesToSendKey = 'sequencesToSend';
export var remoteConfigKey = 'remoteConfig';
export var defineObjectStores = function (db) {
    var sequencesStore;
    var currentSequenceStore;
    if (!db.objectStoreNames.contains(currentSequenceKey)) {
        currentSequenceStore = db.createObjectStore(currentSequenceKey, {
            keyPath: 'sessionId',
        });
    }
    if (!db.objectStoreNames.contains(sequencesToSendKey)) {
        sequencesStore = db.createObjectStore(sequencesToSendKey, {
            keyPath: 'sequenceId',
            autoIncrement: true,
        });
        sequencesStore.createIndex('sessionId', 'sessionId');
    }
    return {
        sequencesStore: sequencesStore,
        currentSequenceStore: currentSequenceStore,
    };
};
export var createStore = function (dbName) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, openDB(dbName, 1, {
                    upgrade: defineObjectStores,
                })];
            case 1: return [2 /*return*/, _a.sent()];
        }
    });
}); };
var SessionReplayEventsIDBStore = /** @class */ (function (_super) {
    __extends(SessionReplayEventsIDBStore, _super);
    function SessionReplayEventsIDBStore(args) {
        var _this = this;
        var _a;
        _this = _super.call(this, args) || this;
        _this.consecutiveFailures = 0;
        _this.hasTriggeredFallback = false;
        _this.getSequencesToSend = function () { return __awaiter(_this, void 0, void 0, function () {
            var errorLogged, sequences, tx, cursor, _a, sessionId, events, e_1;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        errorLogged = false;
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 6, , 7]);
                        sequences = [];
                        tx = this.db.transaction('sequencesToSend');
                        // Attach a catch handler immediately so tx.done rejections (e.g. AbortError after
                        // cursor traversal completes) are always handled without blocking the return path.
                        // The errorLogged flag prevents double-logging when the outer catch already fired
                        // for the same abort (e.g. cursor.continue() threw mid-traversal).
                        tx.done.catch(function (e) {
                            if (!errorLogged) {
                                logIdbError(_this.loggerProvider, "".concat(STORAGE_FAILURE, ": ").concat(e), e);
                            }
                        });
                        return [4 /*yield*/, tx.store.openCursor()];
                    case 2:
                        cursor = _b.sent();
                        _b.label = 3;
                    case 3:
                        if (!cursor) return [3 /*break*/, 5];
                        _a = cursor.value, sessionId = _a.sessionId, events = _a.events;
                        sequences.push({
                            events: events,
                            sequenceId: cursor.key,
                            sessionId: sessionId,
                        });
                        return [4 /*yield*/, cursor.continue()];
                    case 4:
                        cursor = _b.sent();
                        return [3 /*break*/, 3];
                    case 5:
                        this.recordSuccess();
                        return [2 /*return*/, sequences];
                    case 6:
                        e_1 = _b.sent();
                        errorLogged = true;
                        logIdbError(this.loggerProvider, "".concat(STORAGE_FAILURE, ": ").concat(e_1), e_1);
                        this.recordFailure();
                        return [3 /*break*/, 7];
                    case 7: return [2 /*return*/, undefined];
                }
            });
        }); };
        _this.storeCurrentSequence = function (sessionId) { return __awaiter(_this, void 0, void 0, function () {
            var currentSequenceData, sequenceId, e_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 4, , 5]);
                        return [4 /*yield*/, this.db.get(currentSequenceKey, sessionId)];
                    case 1:
                        currentSequenceData = _a.sent();
                        if (!currentSequenceData) {
                            this.recordSuccess();
                            return [2 /*return*/, undefined];
                        }
                        return [4 /*yield*/, this.db.put(sequencesToSendKey, {
                                sessionId: sessionId,
                                events: currentSequenceData.events,
                            })];
                    case 2:
                        sequenceId = _a.sent();
                        return [4 /*yield*/, this.db.put(currentSequenceKey, {
                                sessionId: sessionId,
                                events: [],
                            })];
                    case 3:
                        _a.sent();
                        this.recordSuccess();
                        return [2 /*return*/, __assign(__assign({}, currentSequenceData), { sessionId: sessionId, sequenceId: sequenceId })];
                    case 4:
                        e_2 = _a.sent();
                        logIdbError(this.loggerProvider, "".concat(STORAGE_FAILURE, ": ").concat(e_2), e_2);
                        this.recordFailure();
                        return [3 /*break*/, 5];
                    case 5: return [2 /*return*/, undefined];
                }
            });
        }); };
        _this.addEventToCurrentSequence = function (sessionId, event) { return __awaiter(_this, void 0, void 0, function () {
            var errorLogged, tx, sequenceEvents, eventsToSend, sequenceId, e_3;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        errorLogged = false;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 9, , 10]);
                        tx = this.db.transaction([currentSequenceKey, sequencesToSendKey], 'readwrite');
                        // Attach a catch handler immediately so tx.done rejections (e.g. AbortError after
                        // put succeeds but before auto-commit) are always handled without blocking.
                        // The errorLogged flag prevents double-logging when the outer catch already fired
                        // for the same abort (e.g. a put() threw).
                        tx.done.catch(function (e) {
                            if (!errorLogged) {
                                logIdbError(_this.loggerProvider, "".concat(STORAGE_FAILURE, ": ").concat(e), e);
                            }
                        });
                        return [4 /*yield*/, tx.objectStore(currentSequenceKey).get(sessionId)];
                    case 2:
                        sequenceEvents = _a.sent();
                        if (!!sequenceEvents) return [3 /*break*/, 4];
                        return [4 /*yield*/, tx.objectStore(currentSequenceKey).put({ sessionId: sessionId, events: [event] })];
                    case 3:
                        _a.sent();
                        this.recordSuccess();
                        return [2 /*return*/, undefined];
                    case 4:
                        if (!!this.shouldSplitEventsList(sequenceEvents.events, event)) return [3 /*break*/, 6];
                        return [4 /*yield*/, tx.objectStore(currentSequenceKey).put({ sessionId: sessionId, events: sequenceEvents.events.concat(event) })];
                    case 5:
                        _a.sent();
                        this.recordSuccess();
                        return [2 /*return*/, undefined];
                    case 6:
                        eventsToSend = sequenceEvents.events;
                        return [4 /*yield*/, tx.objectStore(currentSequenceKey).put({ sessionId: sessionId, events: [event] })];
                    case 7:
                        _a.sent();
                        return [4 /*yield*/, tx.objectStore(sequencesToSendKey).put({
                                sessionId: sessionId,
                                events: eventsToSend,
                            })];
                    case 8:
                        sequenceId = _a.sent();
                        this.recordSuccess();
                        return [2 /*return*/, {
                                events: eventsToSend,
                                sessionId: sessionId,
                                sequenceId: sequenceId,
                            }];
                    case 9:
                        e_3 = _a.sent();
                        errorLogged = true;
                        logIdbError(this.loggerProvider, "".concat(STORAGE_FAILURE, ": ").concat(e_3), e_3);
                        this.recordFailure();
                        return [3 /*break*/, 10];
                    case 10: return [2 /*return*/, undefined];
                }
            });
        }); };
        _this.storeSendingEvents = function (sessionId, events) { return __awaiter(_this, void 0, void 0, function () {
            var sequenceId, e_4;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.db.put(sequencesToSendKey, {
                                sessionId: sessionId,
                                events: events,
                            })];
                    case 1:
                        sequenceId = _a.sent();
                        this.recordSuccess();
                        return [2 /*return*/, sequenceId];
                    case 2:
                        e_4 = _a.sent();
                        logIdbError(this.loggerProvider, "".concat(STORAGE_FAILURE, ": ").concat(e_4), e_4);
                        this.recordFailure();
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/, undefined];
                }
            });
        }); };
        _this.cleanUpSessionEventsStore = function (_sessionId, sequenceId) { return __awaiter(_this, void 0, void 0, function () {
            var e_5;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!sequenceId) {
                            return [2 /*return*/];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.db.delete(sequencesToSendKey, sequenceId)];
                    case 2:
                        _a.sent();
                        this.recordSuccess();
                        return [3 /*break*/, 4];
                    case 3:
                        e_5 = _a.sent();
                        logIdbError(this.loggerProvider, "".concat(STORAGE_FAILURE, ": ").concat(e_5), e_5);
                        this.recordFailure();
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        }); };
        _this.db = args.db;
        _this.onPersistentFailure = args.onPersistentFailure;
        _this.consecutiveFailureThreshold = (_a = args.consecutiveFailureThreshold) !== null && _a !== void 0 ? _a : 3;
        return _this;
    }
    SessionReplayEventsIDBStore.prototype.recordFailure = function () {
        var _a;
        this.consecutiveFailures++;
        if (!this.hasTriggeredFallback && this.consecutiveFailures >= this.consecutiveFailureThreshold) {
            this.hasTriggeredFallback = true;
            (_a = this.onPersistentFailure) === null || _a === void 0 ? void 0 : _a.call(this);
        }
    };
    SessionReplayEventsIDBStore.prototype.recordSuccess = function () {
        this.consecutiveFailures = 0;
    };
    SessionReplayEventsIDBStore.new = function (type, args) {
        return __awaiter(this, void 0, void 0, function () {
            var dbSuffix, dbName, db, e_6;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        dbSuffix = type === 'replay' ? '' : "_".concat(type);
                        dbName = "".concat(args.apiKey.substring(0, 10), "_amp_session_replay_events").concat(dbSuffix);
                        return [4 /*yield*/, createStore(dbName)];
                    case 1:
                        db = _a.sent();
                        return [2 /*return*/, new SessionReplayEventsIDBStore(__assign(__assign({}, args), { db: db }))];
                    case 2:
                        e_6 = _a.sent();
                        logIdbError(args.loggerProvider, "".concat(STORAGE_FAILURE, ": ").concat(e_6), e_6);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    SessionReplayEventsIDBStore.prototype.getCurrentSequenceEvents = function (sessionId) {
        return __awaiter(this, void 0, void 0, function () {
            var events, allEvents, _a, _b, events, e_7_1;
            var e_7, _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        if (!sessionId) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.db.get('sessionCurrentSequence', sessionId)];
                    case 1:
                        events = _d.sent();
                        if (!events) {
                            return [2 /*return*/, undefined];
                        }
                        return [2 /*return*/, [events]];
                    case 2:
                        allEvents = [];
                        _d.label = 3;
                    case 3:
                        _d.trys.push([3, 8, 9, 10]);
                        return [4 /*yield*/, this.db.getAll('sessionCurrentSequence')];
                    case 4:
                        _a = __values.apply(void 0, [_d.sent()]), _b = _a.next();
                        _d.label = 5;
                    case 5:
                        if (!!_b.done) return [3 /*break*/, 7];
                        events = _b.value;
                        allEvents.push(events);
                        _d.label = 6;
                    case 6:
                        _b = _a.next();
                        return [3 /*break*/, 5];
                    case 7: return [3 /*break*/, 10];
                    case 8:
                        e_7_1 = _d.sent();
                        e_7 = { error: e_7_1 };
                        return [3 /*break*/, 10];
                    case 9:
                        try {
                            if (_b && !_b.done && (_c = _a.return)) _c.call(_a);
                        }
                        finally { if (e_7) throw e_7.error; }
                        return [7 /*endfinally*/];
                    case 10: return [2 /*return*/, allEvents];
                }
            });
        });
    };
    return SessionReplayEventsIDBStore;
}(BaseEventsStore));
export { SessionReplayEventsIDBStore };
//# sourceMappingURL=events-idb-store.js.map